import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  XCircle,
  Clock,
  Mail,
  Home,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 20; // ~40s at 2s interval
const POLL_INTERVAL_MS = 2000;

const formatAmount = (amount, currency) => {
  if (amount == null) return "";
  try {
    // PayPal amount is a decimal string (e.g. "3599.00")
    const value = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || ""} ${amount}`;
  }
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id") || params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", data: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const toastedRef = useRef(false);

  useEffect(() => {
    if (!orderId) {
      setStatus({ state: "error", data: { message: "Missing order id" } });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(
          `${API}/payments/order/${orderId}`,
        );
        if (cancelled) return;

        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
          if (!toastedRef.current) {
            toastedRef.current = true;
            toast.success("Payment confirmed. Thank you.");
          }
          return;
        }
        if (data.payment_status === "failed") {
          setStatus({ state: "expired", data });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({ state: "timeout", data });
          return;
        }
        setStatus({ state: "pending", data });
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        // Short-circuit on 404 — invalid/unknown order, no point retrying
        if (err?.response?.status === 404) {
          setStatus({
            state: "error",
            data: {
              message: "This order is not recognised.",
            },
          });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({
            state: "error",
            data: {
              message:
                err?.response?.data?.detail ||
                "Unable to verify payment status.",
            },
          });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [orderId]);

  const s = status.state;
  const d = status.data || {};

  return (
    <main
      data-testid="payment-success-page"
      className="min-h-screen bg-[#040812] pt-32 pb-24 px-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-500/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-2xl mx-auto">
        <div className="bg-[#0B132B]/50 border border-white/10 p-8 md:p-12">
          {s === "checking" || s === "pending" ? (
            <>
              <Loader2 size={28} className="text-red-500 animate-spin" />
              <span className="overline mt-8 block">
                Verifying with Stripe
              </span>
              <h1 className="mt-4 font-display text-white text-3xl md:text-4xl tracking-tighter font-light leading-tight">
                Confirming your payment…
              </h1>
              <p className="mt-4 text-slate-400 text-sm">
                This usually takes just a few seconds. Please don&apos;t close this
                tab.
              </p>
            </>
          ) : null}

          {s === "paid" && (
            <>
              <CheckCircle2
                size={28}
                className="text-red-500"
                strokeWidth={1.5}
              />
              <span className="overline mt-8 block">Payment received</span>
              <h1 className="mt-4 font-display text-white text-3xl md:text-4xl tracking-tighter font-light leading-tight">
                Thank you — you&apos;re on the founding list.
              </h1>

              <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 border-t border-white/10 pt-6 text-sm">
                <div>
                  <dt className="overline mb-1">Product</dt>
                  <dd className="text-white">{d.product_name || "—"}</dd>
                </div>
                <div>
                  <dt className="overline mb-1">Amount paid</dt>
                  <dd className="text-white">
                    {formatAmount(d.amount, d.currency)}
                  </dd>
                </div>
                <div>
                  <dt className="overline mb-1">Order reference</dt>
                  <dd className="text-white font-mono text-xs break-all">
                    {orderId}
                  </dd>
                </div>
                <div>
                  <dt className="overline mb-1">Next step</dt>
                  <dd className="text-white">
                    A partner will email you within 1 business day.
                  </dd>
                </div>
              </dl>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium px-6 py-3.5 rounded-sm transition-colors"
                >
                  <Home size={14} /> Back to home
                </Link>
                <a
                  href="mailto:Info@srimansolutions.com"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-white text-sm px-6 py-3.5 rounded-sm transition-colors"
                >
                  <Mail size={14} /> Contact us
                </a>
              </div>
            </>
          )}

          {(s === "expired" || s === "timeout" || s === "error") && (
            <>
              {s === "expired" ? (
                <Clock size={28} className="text-red-500" />
              ) : (
                <XCircle size={28} className="text-red-500" />
              )}
              <span className="overline mt-8 block">
                {s === "expired" ? "Session expired" : "Verification issue"}
              </span>
              <h1 className="mt-4 font-display text-white text-3xl md:text-4xl tracking-tighter font-light leading-tight">
                {s === "expired"
                  ? "Your checkout session has expired."
                  : s === "timeout"
                    ? "We couldn't confirm your payment yet."
                    : "We couldn't verify this session."}
              </h1>
              <p className="mt-4 text-slate-400 text-sm">
                {s === "expired"
                  ? "No charge was made. Please restart the pre-booking from the Products page."
                  : "Your bank may still complete this transaction. Reach out to us with the reference below and we'll confirm manually."}
              </p>
              {orderId && (
                <div className="mt-6 text-slate-500 font-mono text-xs break-all">
                  Reference: {orderId}
                </div>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/products")}
                  className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium px-6 py-3.5 rounded-sm transition-colors"
                >
                  Back to Products
                  <ArrowRight size={14} />
                </button>
                <a
                  href="mailto:Info@srimansolutions.com?subject=Payment%20verification%20assistance"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-white text-sm px-6 py-3.5 rounded-sm transition-colors"
                >
                  <Mail size={14} /> Email support
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
